import {
	DELIVERY_METHOD_KIND,
	type DeliveryMethodKind,
	deliveryMethodKindsSchema,
	type MarketName,
	MarketNames,
	resolveDeliveryMethodKinds,
	VENDOR_AGGREGATION_GROUP,
	type VendorAggregation,
	type VendorListItem,
} from "@dransay/api/models";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	useNavigate,
	useParams,
	useRouteLoaderData,
	useSearchParams,
} from "react-router";
import { filterSchemaParser } from "~/features/products/filters/utils";
import { useCurrentRoute } from "~/hooks/use-current-route";
import { useMarket } from "~/hooks/use-market";
import { useVendor } from "~/hooks/use-vendor.hook";
import { defaultVendorParamsByMarket } from "~/routes/helpers/default-vendor-params-by-market";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { pharmacySelectedEvent } from "~/tooling/telemetry/events";
import { VENDOR_PARAMS } from "~/utils/vendor";
import { useCart } from "../features/cart/cart.context";
import type { Route } from "../routes/+types/_storefront";

interface VendorSelectionState {
	id?: string;
	deliveryMethodKind?: DeliveryMethodKind;
}

export interface PreviousVendor {
	id?: string;
	name?: string | null;
	deliveryMethodKind?: DeliveryMethodKind;
	availableDeliveryMethods?: DeliveryMethodKind[];
}

interface VendorSelectionContextType {
	selectedVendorId?: string;
	selectedDeliveryMethodKind?: DeliveryMethodKind;
	previousVendor?: PreviousVendor;
	vendor?: VendorListItem;
	isLoading: boolean;
	setSelectedVendor: (vendorId?: string) => Promise<void>;
	setSelectedDeliveryMethodKind: (kind?: DeliveryMethodKind) => Promise<void>;
	/**
	 * Atomically switch pharmacy and delivery method. Prefer this over calling
	 * setSelectedVendor then setSelectedDeliveryMethodKind — the latter can
	 * fall back to the market default when the newly selected vendor's methods
	 * are not loaded yet.
	 */
	setSelectedVendorAndDeliveryMethod: (
		vendorId: string,
		deliveryMethodKind: DeliveryMethodKind,
	) => Promise<void>;
	restorePreviousSelection: () => Promise<void>;
	isCartVendor: boolean;
}

const getDefaultVendorId = (
	market: MarketName,
	deliveryMethod: DeliveryMethodKind | undefined,
	enableTopPharmacyCartPromotion = false,
): VendorAggregation => {
	switch (market) {
		case MarketNames.de:
			// Courier and Abholung have no Top/non-Top split at all: their list is
			// simply every pharmacy. Defaulting them to TOP under the promotion
			// flag would scope the list to Top pharmacies with no visible control
			// to widen it again, since those surfaces show no Top group row.
			if (deliveryMethod !== DELIVERY_METHOD_KIND.SHIPPING) {
				return VENDOR_AGGREGATION_GROUP.ALL;
			}
			return VENDOR_AGGREGATION_GROUP.TOP;
	}
};

const getDefaultVendorName = (
	market: MarketName,
	id: VendorAggregation,
	deliveryMethodKind?: DeliveryMethodKind,
) => {
	switch (true) {
		case deliveryMethodKind === DELIVERY_METHOD_KIND.SHIPPING &&
			id === VENDOR_AGGREGATION_GROUP.TOP:
			return "vendor.group.topShipping";
		case deliveryMethodKind === DELIVERY_METHOD_KIND.SHIPPING &&
			id === VENDOR_AGGREGATION_GROUP.ALL:
			return "vendor.group.allShipping";
		case deliveryMethodKind === DELIVERY_METHOD_KIND.PICKUP &&
			id === VENDOR_AGGREGATION_GROUP.TOP:
			return "vendor.group.topPickup";
		case deliveryMethodKind === DELIVERY_METHOD_KIND.PICKUP &&
			id === VENDOR_AGGREGATION_GROUP.ALL:
			return "vendor.group.allPickup";
		case deliveryMethodKind === DELIVERY_METHOD_KIND.COURIER &&
			id === VENDOR_AGGREGATION_GROUP.TOP:
			return "vendor.group.topCourier";
		case deliveryMethodKind === DELIVERY_METHOD_KIND.COURIER &&
			id === VENDOR_AGGREGATION_GROUP.ALL:
			return "vendor.group.allCourier";
		default:
			return "vendor.group.topShipping";
	}
};

const getAvailableDeliveryMethods = (v?: VendorListItem) => {
	if (!v?.deliveryMethods) return [];
	return v.deliveryMethods
		.filter((method) => method.enabled)
		.map((method) => method.kind);
};

/**
 * Aggregation groups ("top"/"all") only exist for shipping/courier/pickup --
 * express-courier has none (see getAggregatedVendorOptions). A previousVendor
 * entry must never remember a method its target (a concrete vendor or an
 * aggregation group) can't actually serve, or restoring it dead-ends in a 404.
 *
 * Only call this with an availableMethods list that is known to be
 * non-empty for the target it will be paired with -- an empty list falls
 * back to DEFAULT_DELIVERY_METHOD_KIND, which is meaningless for a concrete
 * vendor that doesn't offer it (e.g. an express-courier-only vendor). Use
 * getDefaultAggregationTarget for that case instead.
 */
const clampToSupportedMethod = (
	requestedKind: DeliveryMethodKind | undefined,
	availableMethods: DeliveryMethodKind[],
): DeliveryMethodKind | undefined => {
	if (requestedKind && availableMethods.includes(requestedKind)) {
		return requestedKind;
	}
	return availableMethods[0] ?? DEFAULT_DELIVERY_METHOD_KIND;
};

const AGGREGATABLE_DELIVERY_METHODS: DeliveryMethodKind[] = [
	DELIVERY_METHOD_KIND.SHIPPING,
	DELIVERY_METHOD_KIND.COURIER,
	DELIVERY_METHOD_KIND.PICKUP,
];

/**
 * The aggregation-group ("top"/"all") target for a requested delivery
 * method -- always resolvable since aggregation groups cover
 * shipping/courier/pickup. Use this whenever the natural target (a concrete
 * cart vendor) has no browsable method of its own, instead of pairing that
 * vendor's id with a method it doesn't offer.
 *
 * Returns a translation key (rather than resolving it) so callers can pass
 * their own `t` -- i18next's TFunction type has enough overloads that
 * accepting it as a plain parameter here trips a TypeScript checker crash.
 */
const getDefaultAggregationTarget = (
	requestedKind: DeliveryMethodKind | undefined,
	market: MarketName,
	enableTopPharmacyCartPromotion: boolean,
): Omit<PreviousVendor, "name"> & {
	nameKey: ReturnType<typeof getDefaultVendorName>;
} => {
	const deliveryMethodKind = clampToSupportedMethod(
		requestedKind,
		AGGREGATABLE_DELIVERY_METHODS,
	);
	const id = getDefaultVendorId(
		market,
		deliveryMethodKind,
		enableTopPharmacyCartPromotion,
	);
	return {
		id,
		nameKey: getDefaultVendorName(market, id, deliveryMethodKind),
		deliveryMethodKind,
		availableDeliveryMethods: AGGREGATABLE_DELIVERY_METHODS,
	};
};

/**
 * Browsable methods for a concrete vendor, i.e. its enabled methods minus
 * express-courier. A previousVendor entry is only ever consumed by browsing
 * navigation (restorePreviousSelection, "back to"/"go to" links), so even a
 * cart vendor that genuinely offers express-courier must never have it show
 * up here -- storing it would let it flow back into the browsing URL (FR-065).
 */
const getBrowsableDeliveryMethods = (v?: VendorListItem) =>
	getAvailableDeliveryMethods(v).filter((method) =>
		AGGREGATABLE_DELIVERY_METHODS.includes(method),
	);

const VendorSelectionContext = createContext<
	VendorSelectionContextType | undefined
>(undefined);

const DEFAULT_DELIVERY_METHOD_KIND: DeliveryMethodKind =
	DELIVERY_METHOD_KIND.SHIPPING;

function useResetToDefaultOnCartClear({
	currentVendorId,
	onClear,
}: {
	currentVendorId: string | undefined;
	onClear: () => void;
}) {
	const { vendor: cartVendor, items: cartItems } = useCart();

	const clearedCartData = useRef({
		vendorId: cartVendor?.id,
		itemsCount: cartItems.length,
	});

	useEffect(() => {
		if (
			!cartItems.length &&
			clearedCartData.current.itemsCount > 0 &&
			clearedCartData.current.vendorId === currentVendorId
		) {
			onClear();
		}

		clearedCartData.current = {
			vendorId: cartVendor?.id,
			itemsCount: cartItems.length,
		};
	}, [cartItems.length, currentVendorId, onClear, cartVendor?.id]);
}

function isCurrentVendorListing(
	newVendor: VendorSelectionState,
	routeParams: { vendorId?: string; deliveryMethod?: string },
): boolean {
	if (
		routeParams.vendorId === newVendor.id &&
		routeParams.deliveryMethod === newVendor.deliveryMethodKind
	) {
		return true;
	}
	if (window.location.pathname !== "/products") {
		return false;
	}
	const currentQuery = new URLSearchParams(window.location.search);
	return (
		currentQuery.get(VENDOR_PARAMS.vendorId) === (newVendor.id ?? null) &&
		currentQuery.get(VENDOR_PARAMS.deliveryMethod) ===
			(newVendor.deliveryMethodKind ?? null)
	);
}

function useVendorSelectionState(initialVendor?: VendorSelectionState | null) {
	const { t } = useTranslation("product");
	const market = useMarket();
	const [searchParams] = useSearchParams();
	const storefrontLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>(
			"routes/_storefront",
		);
	const enableTopPharmacyCartPromotion =
		storefrontLoaderData?.enableTopPharmacyCartPromotion ?? false;
	// Delivery-address cookie, not the URL. Without it, express-courier is
	// not courier, and keeping the pharmacy here only reaches the loader
	// fallback a navigation later.
	const hasDeliveryAddress = Boolean(
		storefrontLoaderData?.deliveryAddress?.postalCode?.trim(),
	);

	const [vendor, setVendorState] = useState<VendorSelectionState>(() => {
		if (initialVendor) return initialVendor;
		const urlVendorId = searchParams.get(VENDOR_PARAMS.vendorId);
		const urlDeliveryMethod = deliveryMethodKindsSchema.safeParse(
			searchParams.get(VENDOR_PARAMS.deliveryMethod),
		).data;
		if (urlVendorId && urlDeliveryMethod) {
			return { id: urlVendorId, deliveryMethodKind: urlDeliveryMethod };
		}
		return {
			deliveryMethodKind: DEFAULT_DELIVERY_METHOD_KIND,
			id: getDefaultVendorId(
				market.code,
				DEFAULT_DELIVERY_METHOD_KIND,
				enableTopPharmacyCartPromotion,
			),
		};
	});
	const defaultVendorId = getDefaultVendorId(
		market.code,
		vendor.deliveryMethodKind,
		enableTopPharmacyCartPromotion,
	);

	const { vendor: cartVendor } = useCart();

	const isVendorAggregationGroupId = useCallback(
		(vendorId?: string) =>
			vendorId &&
			(Object.values(VENDOR_AGGREGATION_GROUP) as string[]).includes(vendorId),
		[],
	);

	const determinePreviousVendorState = useCallback(
		(
			vendorId: string | undefined,
			deliveryMethodKind = vendor.deliveryMethodKind,
		) => {
			if (cartVendor && cartVendor.id !== vendorId) {
				const availableDeliveryMethods =
					getBrowsableDeliveryMethods(cartVendor);
				// A cart vendor whose only enabled method is express-courier has
				// no browsable method at all -- fall through to the aggregation
				// default rather than pairing its concrete id with a method
				// (DEFAULT_DELIVERY_METHOD_KIND) it doesn't actually offer.
				if (availableDeliveryMethods.length > 0) {
					return {
						id: cartVendor.id,
						name: cartVendor.name,
						deliveryMethodKind: clampToSupportedMethod(
							deliveryMethodKind,
							availableDeliveryMethods,
						),
						availableDeliveryMethods,
					};
				}
			}

			const { nameKey, ...target } = getDefaultAggregationTarget(
				deliveryMethodKind,
				market.code,
				enableTopPharmacyCartPromotion,
			);
			return { ...target, name: t(nameKey) };
		},
		[cartVendor, market, vendor, t, enableTopPharmacyCartPromotion],
	);

	const [previousVendor, setPreviousVendor] = useState<PreviousVendor>(
		determinePreviousVendorState(vendor.id),
	);

	const isCartVendor = cartVendor?.id === vendor.id;

	const navigate = useNavigate();
	const { vendorId: routeVendorId, deliveryMethod: routeDeliveryMethod } =
		useParams();
	const { isProductsRoute } = useCurrentRoute();

	const setVendor = useCallback(
		async (newVendor: VendorSelectionState, filtersOverride?: string) => {
			const queryParams = new URLSearchParams(window.location.search);
			if (newVendor.id) {
				queryParams.set(VENDOR_PARAMS.vendorId, newVendor.id);
			} else {
				queryParams.delete(VENDOR_PARAMS.vendorId);
			}
			if (newVendor.deliveryMethodKind) {
				queryParams.set(
					VENDOR_PARAMS.deliveryMethod,
					newVendor.deliveryMethodKind,
				);
			} else {
				queryParams.delete(VENDOR_PARAMS.deliveryMethod);
			}

			if (filtersOverride !== undefined) {
				if (filtersOverride && filtersOverride !== "{}") {
					queryParams.set("filters", filtersOverride);
				} else {
					queryParams.delete("filters");
				}
			}

			// Navigate first so location.search is updated before state triggers re-renders.
			// This ensures product card URLs (built from location.search) reflect the new vendor.
			if (!isProductsRoute()) {
				await navigate(
					`${window.location.pathname}?${queryParams.toString()}`,
					{ replace: true },
				);
			} else if (
				// Replacing /products with the same query aborts the 301 onto the
				// dedicated vendor listing and refetches products.data forever.
				!isCurrentVendorListing(newVendor, {
					vendorId: routeVendorId,
					deliveryMethod: routeDeliveryMethod,
				})
			) {
				await navigate(`/products?${queryParams.toString()}`, {
					replace: true,
				});
			}

			setVendorState(newVendor);
		},
		[navigate, isProductsRoute, routeDeliveryMethod, routeVendorId],
	);

	useResetToDefaultOnCartClear({
		currentVendorId: vendor.id,
		onClear: () => {
			const defaultParams = defaultVendorParamsByMarket[market.code];
			const defaultDeliveryMethodKind =
				defaultParams?.deliveryMethod ?? vendor.deliveryMethodKind;
			const defaultVendorIdForMarket = getDefaultVendorId(
				market.code,
				defaultDeliveryMethodKind,
				enableTopPharmacyCartPromotion,
			);
			const serializedFilters = filterSchemaParser.serialize(
				defaultParams?.filters ?? {},
			);
			const newVendorState = {
				...vendor,
				id: defaultVendorIdForMarket,
				deliveryMethodKind: defaultDeliveryMethodKind,
			};
			setPreviousVendor(
				determinePreviousVendorState(
					defaultVendorIdForMarket,
					defaultDeliveryMethodKind,
				),
			);
			setVendor(newVendorState, serializedFilters);
		},
	});

	const vendorData = useVendor({
		vendorId: vendor.id,
		deliveryMethodKind: vendor.deliveryMethodKind,
	});

	const pendingPharmacySelected = useRef<{
		vendorId: string;
		deliveryMethodKind: DeliveryMethodKind;
	} | null>(null);

	const resolvePharmacySource = useCallback(
		(vendorId: string) => {
			if (vendorData.vendor?.id === vendorId) {
				return vendorData.vendor;
			}
			if (cartVendor?.id === vendorId) {
				return cartVendor;
			}
			if (isVendorAggregationGroupId(vendorId)) {
				return {
					name: "",
					isTop: vendorId === VENDOR_AGGREGATION_GROUP.TOP,
				};
			}
			return null;
		},
		[cartVendor, isVendorAggregationGroupId, vendorData.vendor],
	);

	const capturePharmacySelected = useCallback(
		(
			vendorId: string,
			deliveryMethodKind: DeliveryMethodKind,
			source: { name?: string | null; isTop?: boolean | null },
		) => {
			const pharmacyType = source.isTop
				? VENDOR_AGGREGATION_GROUP.TOP
				: vendorId === VENDOR_AGGREGATION_GROUP.TOP
					? VENDOR_AGGREGATION_GROUP.TOP
					: VENDOR_AGGREGATION_GROUP.ALL;

			captureEvent(
				pharmacySelectedEvent(
					vendorId,
					source.name ?? "",
					pharmacyType,
					deliveryMethodKind,
				),
			);
		},
		[],
	);

	const queuePharmacySelected = useCallback(
		(vendorId: string, deliveryMethodKind?: DeliveryMethodKind) => {
			const method =
				deliveryMethodKind ??
				vendor.deliveryMethodKind ??
				DEFAULT_DELIVERY_METHOD_KIND;
			const source = resolvePharmacySource(vendorId);
			if (!source) {
				pendingPharmacySelected.current = {
					vendorId,
					deliveryMethodKind: method,
				};
				return;
			}
			pendingPharmacySelected.current = null;
			capturePharmacySelected(vendorId, method, source);
		},
		[capturePharmacySelected, resolvePharmacySource, vendor.deliveryMethodKind],
	);

	useEffect(() => {
		const pending = pendingPharmacySelected.current;
		if (!pending) {
			return;
		}
		// Selection moved away (e.g. restore) — drop the abandoned claim so a
		// stale useVendor fetch for the old id cannot flush later.
		if (vendor.id !== pending.vendorId) {
			pendingPharmacySelected.current = null;
			return;
		}
		if (vendorData.vendor?.id !== pending.vendorId) {
			return;
		}
		pendingPharmacySelected.current = null;
		capturePharmacySelected(
			pending.vendorId,
			pending.deliveryMethodKind,
			vendorData.vendor,
		);
	}, [vendor.id, vendorData.vendor, capturePharmacySelected]);

	const handleVendorChange = useCallback(
		async (vendorId?: string) => {
			const nextVendorId = vendorId || defaultVendorId;
			// skip if the effective vendor is already selected
			if (nextVendorId === vendor.id) return;

			const previousVendorToSet = determinePreviousVendorState(vendorId);
			setPreviousVendor(previousVendorToSet);

			await setVendor({ ...vendor, id: nextVendorId });
			queuePharmacySelected(nextVendorId);
		},
		[
			defaultVendorId,
			setVendor,
			vendor,
			determinePreviousVendorState,
			queuePharmacySelected,
		],
	);

	const handleDeliveryMethodChange = useCallback(
		async (deliveryMethodKind?: DeliveryMethodKind) => {
			const selectedVendor =
				vendor.id === cartVendor?.id ? cartVendor : vendorData.vendor;

			const enabledKinds =
				selectedVendor?.deliveryMethods
					?.filter((method) => method.enabled)
					.map((method) => method.kind) ?? [];
			const kindsForSelection =
				resolveDeliveryMethodKinds(deliveryMethodKind, hasDeliveryAddress) ??
				(deliveryMethodKind ? [deliveryMethodKind] : []);
			const vendorSupportsDeliveryMethod = kindsForSelection.some((kind) =>
				enabledKinds.includes(kind),
			);

			const defaultVendorId = getDefaultVendorId(
				market.code,
				deliveryMethodKind,
				enableTopPharmacyCartPromotion,
			);

			if (
				!vendorSupportsDeliveryMethod &&
				!isVendorAggregationGroupId(vendor.id)
			) {
				setPreviousVendor({
					id: vendor.id,
					name: vendorData.vendor?.name,
					deliveryMethodKind: vendor.deliveryMethodKind,
					availableDeliveryMethods: getBrowsableDeliveryMethods(
						vendorData.vendor,
					),
				});
			} else if (vendorSupportsDeliveryMethod) {
				setPreviousVendor(
					determinePreviousVendorState(vendor.id, deliveryMethodKind),
				);
			}

			let id: string = defaultVendorId;
			if (vendorSupportsDeliveryMethod && selectedVendor) {
				id = selectedVendor.id;
			}

			await setVendor({ ...vendor, id, deliveryMethodKind });
		},
		[
			setVendor,
			cartVendor,
			vendor,
			vendorData.vendor,
			market.code,
			determinePreviousVendorState,
			isVendorAggregationGroupId,
			enableTopPharmacyCartPromotion,
			hasDeliveryAddress,
		],
	);

	const handleVendorAndDeliveryMethodChange = useCallback(
		async (vendorId: string, deliveryMethodKind: DeliveryMethodKind) => {
			setPreviousVendor(
				determinePreviousVendorState(vendorId, deliveryMethodKind),
			);
			await setVendor({ id: vendorId, deliveryMethodKind });
			queuePharmacySelected(vendorId, deliveryMethodKind);
		},
		[setVendor, determinePreviousVendorState, queuePharmacySelected],
	);

	const restorePreviousSelection = useCallback(async () => {
		if (!previousVendor.id) {
			return;
		}

		const availableMethods = previousVendor.availableDeliveryMethods || [];

		// The remembered target has no browsable method of its own (e.g. it
		// was a cart vendor offering only express-courier) -- restoring its
		// concrete id would dead-end, so land on the aggregation default
		// instead of pairing that id with an unsupported method.
		if (availableMethods.length === 0) {
			const target = getDefaultAggregationTarget(
				previousVendor.deliveryMethodKind,
				market.code,
				enableTopPharmacyCartPromotion,
			);
			await setVendor({
				id: target.id,
				deliveryMethodKind: target.deliveryMethodKind,
			});
			setPreviousVendor(determinePreviousVendorState(target.id));
			return;
		}

		const currentMethodSupported =
			!!vendor.deliveryMethodKind &&
			availableMethods.includes(vendor.deliveryMethodKind);

		// Prefer staying on the currently browsed method when the restored
		// target actually supports it; otherwise fall back to a method the
		// target can serve (never a remembered-but-unsupported one, e.g.
		// express-courier on a vendor/group that doesn't offer it).
		const deliveryMethodKind = currentMethodSupported
			? vendor.deliveryMethodKind
			: clampToSupportedMethod(
					previousVendor.deliveryMethodKind,
					availableMethods,
				);

		await setVendor({
			id: previousVendor.id,
			deliveryMethodKind,
		});

		setPreviousVendor(determinePreviousVendorState(previousVendor.id));
	}, [
		setVendor,
		previousVendor,
		vendor,
		determinePreviousVendorState,
		market,
		enableTopPharmacyCartPromotion,
	]);

	return {
		selectedVendorId: vendor.id,
		selectedDeliveryMethodKind:
			vendor.deliveryMethodKind ?? DELIVERY_METHOD_KIND.SHIPPING,
		previousVendor,
		handleVendorChange,
		handleDeliveryMethodChange,
		handleVendorAndDeliveryMethodChange,
		restorePreviousSelection,
		vendorData,
		isCartVendor,
	};
}

export function VendorSelectionProvider({
	children,
	initialVendor,
	initialVendorData,
}: {
	children: React.ReactNode;
	initialVendor?: VendorSelectionState | null;
	initialVendorData: VendorListItem | null;
}) {
	const {
		selectedVendorId,
		selectedDeliveryMethodKind,
		previousVendor,
		handleVendorChange,
		handleDeliveryMethodChange,
		handleVendorAndDeliveryMethodChange,
		restorePreviousSelection,
		vendorData,
		isCartVendor,
	} = useVendorSelectionState(initialVendor);

	const context: VendorSelectionContextType = useMemo(
		() => ({
			selectedVendorId,
			selectedDeliveryMethodKind,
			previousVendor,
			vendor: selectedVendorId
				? (vendorData.vendor ?? initialVendorData ?? undefined)
				: undefined,
			isLoading: vendorData.isLoading,
			setSelectedVendor: handleVendorChange,
			setSelectedDeliveryMethodKind: handleDeliveryMethodChange,
			setSelectedVendorAndDeliveryMethod: handleVendorAndDeliveryMethodChange,
			restorePreviousSelection,
			isCartVendor,
		}),
		[
			selectedVendorId,
			selectedDeliveryMethodKind,
			previousVendor,
			vendorData.vendor,
			vendorData.isLoading,
			initialVendorData,
			handleVendorChange,
			handleDeliveryMethodChange,
			handleVendorAndDeliveryMethodChange,
			restorePreviousSelection,
			isCartVendor,
		],
	);

	return (
		<VendorSelectionContext.Provider value={context}>
			{children}
		</VendorSelectionContext.Provider>
	);
}

export function useVendorSelection() {
	const context = useContext(VendorSelectionContext);
	if (!context) {
		throw new Error(
			"useVendorSelection must be used within VendorSelectionProvider",
		);
	}
	return context;
}
